import React, { useCallback } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import CardSkeleton from 'components/Intelligence/CardSkeleton';
import { parseErrrorMessage } from 'containers/utils';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { TOrbit } from '../i18n/TModules';
import { orbitRelativeTime } from '../relativeTime';
import { OrbitBodyText, OrbitTextButton, orbitTokens, orbitType } from '../styles';
import { OrbitSession } from '../types';
import useOrbitConversations, { ORBIT_ALL_CHATS } from '../useOrbitConversations';

export interface IOrbitConversationListProps {
  /** Back arrow and row selection both return to the thread view. */
  onClose: () => void;
}

// The `4553:3964` frame's three colours each coincide with a panel token, so the list reads them
// from the token layer rather than repeating the literals (figma-spec.md, layout table).
const frame = {
  title: orbitTokens.threadText,
  chipText: orbitTokens.threadMuted,
  backBorder: orbitTokens.bubbleBorder
} as const;

const Head = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16.8px;
  border-bottom: ${orbitTokens.borderWidth} solid ${orbitTokens.border};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 10px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: ${orbitTokens.borderWidth} solid ${frame.backBorder};
  background: ${orbitTokens.surface};
  color: ${frame.chipText};
  cursor: pointer;
  font-size: 13px;

  &:hover {
    color: ${frame.title};
  }

  &:focus-visible {
    color: ${frame.title};
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 1px;
  }
`;

const Heading = styled.h3`
  margin: 0;
  color: ${frame.title};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

const Body = styled.div`
  margin-top: 10px;

  > * + * {
    margin-top: 10px;
  }
`;

const Rows = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  > * + * {
    margin-top: 9px;
  }
`;

// TODO(AF-10): the frame draws neither a hover nor a selected row; the surfaceAlt fill is invented.
const Row = styled.button`
  display: block;
  width: 100%;
  padding: 12.8px 13.8px;
  border: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  &:hover,
  &[aria-current='true'] {
    background: ${orbitTokens.surfaceAlt};
  }

  /* A 1.04:1 background swap is not a focus indicator (WCAG 1.4.11 wants 3:1); the ring is. */
  &:focus-visible {
    background: ${orbitTokens.surfaceAlt};
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 1px;
  }
`;

const RowTitle = styled.span`
  display: block;
  overflow: hidden;
  color: ${frame.title};
  ${orbitType.containerTitle}
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.span`
  display: block;
  margin-top: 4px;
  color: ${orbitTokens.textMuted};
  ${orbitType.containerBody}
`;

const Notice = styled(OrbitBodyText)`
  color: ${orbitTokens.textSecondary};
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  > * + * {
    margin-left: 12px;
  }
`;

const ErrorText = styled(OrbitBodyText)`
  color: ${orbitTokens.negative};
`;

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
`;

const OrbitConversationList: React.FC<IOrbitConversationListProps> = ({ onClose }) => {
  const { t } = useMultiLanguageTranslation();
  const { sessionId, sessionStale } = useOrbitContext();
  const { sessions, select, hasMore, isLoading, isLoadingMore, error, loadMore, reload } =
    useOrbitConversations(ORBIT_ALL_CHATS);

  const handleSelect = useCallback(
    (session: OrbitSession) => {
      select(session);
      onClose();
    },
    [select, onClose]
  );

  const showError = error !== undefined && error !== null;
  const heading = t(`${TOrbit}.conversation.heading`);
  const backLabel = t(`${TOrbit}.conversation.back`);

  return (
    <div data-testid="orbit-conversation-list">
      <Head>
        <BackButton
          type="button"
          aria-label={backLabel}
          title={backLabel}
          data-testid="orbit-conversation-list-back"
          onClick={onClose}
        >
          <ArrowLeftOutlined aria-hidden="true" />
        </BackButton>
        <Heading>{heading}</Heading>
      </Head>
      <Body>
        {sessionStale ? (
          <Notice role="status" data-testid="orbit-conversation-list-stale">
            {t(`${TOrbit}.notice.sessionEnded`)}
          </Notice>
        ) : null}
        {isLoading ? <CardSkeleton shape="list" rows={3} /> : null}
        {showError ? (
          <ErrorRow role="alert" data-testid="orbit-conversation-list-error">
            <ErrorText as="span">
              {parseErrrorMessage(error, t(`${TOrbit}.notice.conversationsLoadFailed`))}
            </ErrorText>
            <OrbitTextButton
              type="button"
              data-testid="orbit-conversation-list-retry"
              onClick={reload}
            >
              {t(`${TOrbit}.actions.retry`)}
            </OrbitTextButton>
          </ErrorRow>
        ) : null}
        {!isLoading && !showError && sessions.length === 0 ? (
          <Notice data-testid="orbit-conversation-list-empty">
            {t(`${TOrbit}.notice.noConversations`)}
          </Notice>
        ) : null}
        {sessions.length > 0 ? (
          <Rows aria-label={heading}>
            {sessions.map(session => {
              const when = orbitRelativeTime(session.last_activity_at);

              return (
                <li key={session.session_id}>
                  <Row
                    type="button"
                    aria-current={session.session_id === sessionId ? 'true' : undefined}
                    data-testid="orbit-conversation-row"
                    data-session-id={session.session_id}
                    onClick={() => handleSelect(session)}
                  >
                    <RowTitle>{session.title}</RowTitle>
                    {when !== null ? (
                      <RowMeta data-testid="orbit-conversation-meta">
                        {t(`${TOrbit}.conversation.lastActive`, { when })}
                      </RowMeta>
                    ) : null}
                  </Row>
                </li>
              );
            })}
          </Rows>
        ) : null}
        {hasMore && !showError ? (
          <LoadMoreRow>
            <OrbitTextButton
              type="button"
              disabled={isLoadingMore}
              data-testid="orbit-conversation-list-load-more"
              onClick={loadMore}
            >
              {t(`${TOrbit}.actions.loadMore`)}
            </OrbitTextButton>
          </LoadMoreRow>
        ) : null}
      </Body>
    </div>
  );
};

export default OrbitConversationList;
